import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Tag, Heart, Smartphone, ShoppingCart, Menu, User, LogOut, Package, LayoutDashboard, ShieldCheck } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "../../components/ui/sheet";
import { Logo } from "./ui";
import { NavList } from "./Sidebar";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { useCategories } from "../lib/categories";

const IconLink = ({ to, icon: Icon, label, badge, testid }) => (
  <Link to={to} data-testid={testid} title={label}
    className="relative hidden flex-col items-center gap-0.5 rounded-xl px-2.5 py-1.5 text-[11px] font-medium text-gray-600 transition-colors hover:bg-bo-green/5 hover:text-bo-green md:flex">
    <Icon className="h-5 w-5" />
    <span>{label}</span>
    {badge > 0 && <span className="absolute right-1 top-0 grid h-5 min-w-5 place-items-center rounded-full bg-bo-yellow px-1 text-[11px] font-bold text-bo-ink" data-testid="header-cart-count">{badge}</span>}
  </Link>
);

function AccountMenu() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  if (!user) return (
    <Link to="/entrar" data-testid="header-login-btn" className="flex items-center gap-2 rounded-full bg-bo-green px-4 py-2 text-sm font-semibold text-white hover:bg-bo-deep">
      <User className="h-4 w-4" /> <span className="hidden sm:inline">Entrar</span>
    </Link>
  );
  const initials = user.name.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger data-testid="account-menu-trigger" className="flex items-center gap-2 rounded-full p-1 pr-3 hover:bg-gray-100">
        <span className="grid h-9 w-9 place-items-center rounded-full bg-bo-green text-sm font-bold text-bo-yellow">{initials}</span>
        <span className="hidden text-left text-xs leading-tight sm:block">
          <span className="block text-gray-500">Olá,</span>
          <span className="block max-w-[110px] truncate font-semibold">{user.name.split(" ")[0]}</span>
        </span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="truncate">{user.email}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => nav("/conta/pedidos")} data-testid="menu-orders"><Package className="mr-2 h-4 w-4" />Meus pedidos</DropdownMenuItem>
        <DropdownMenuItem onClick={() => nav("/conta/perfil")} data-testid="menu-profile"><User className="mr-2 h-4 w-4" />Perfil</DropdownMenuItem>
        {user.role === "store" && <DropdownMenuItem onClick={() => nav("/painel-loja")} data-testid="menu-store-panel"><LayoutDashboard className="mr-2 h-4 w-4" />Painel da loja</DropdownMenuItem>}
        {user.is_staff && <DropdownMenuItem onClick={() => nav("/admin")} data-testid="menu-admin"><ShieldCheck className="mr-2 h-4 w-4" />Administração</DropdownMenuItem>}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={async () => { await logout(); nav("/"); }} data-testid="menu-logout"><LogOut className="mr-2 h-4 w-4" />Terminar sessão</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Header() {
  const CATEGORIES = useCategories();
  const nav = useNavigate();
  const { count } = useCart();
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [open, setOpen] = useState(false);
  const submit = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (q.trim()) params.set("q", q.trim());
    if (cat) params.set("categoria", cat);
    nav(`/catalogo?${params.toString()}`);
  };
  return (
    <header className="sticky top-0 z-40 border-b border-gray-100 bg-white/95 backdrop-blur-md" data-testid="site-header">
      <div className="mx-auto flex max-w-[1600px] items-center gap-3 px-3 py-3 sm:px-5">
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger className="rounded-lg p-2 hover:bg-gray-100 lg:hidden" data-testid="mobile-menu-btn"><Menu className="h-5 w-5" /></SheetTrigger>
          <SheetContent side="left" className="w-[270px] overflow-y-auto p-4">
            <SheetTitle className="mb-4"><Logo /></SheetTitle>
            <NavList onNavigate={() => setOpen(false)} />
          </SheetContent>
        </Sheet>
        <Link to="/" className="shrink-0"><Logo /></Link>
        <form onSubmit={submit} className="mx-auto hidden max-w-2xl flex-1 items-center overflow-hidden rounded-full border-2 border-bo-green bg-white md:flex" data-testid="search-form">
          <select value={cat} onChange={(e) => setCat(e.target.value)} data-testid="search-category-select"
            className="h-11 max-w-[180px] border-r border-gray-200 bg-gray-50 px-3 text-sm text-gray-700 outline-none">
            <option value="">Todas</option>
            {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.name}</option>)}
          </select>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Pesquisar produtos, marcas ou lojas..."
            className="h-11 flex-1 px-4 text-sm outline-none" data-testid="search-input" />
          <button type="submit" className="grid h-11 w-14 place-items-center bg-bo-green text-white hover:bg-bo-deep" data-testid="search-submit-btn">
            <Search className="h-5 w-5" />
          </button>
        </form>
        <div className="ml-auto flex items-center gap-1 md:ml-0">
          <IconLink to="/ofertas" icon={Tag} label="Ofertas" testid="header-deals-link" />
          <IconLink to="/favoritos" icon={Heart} label="Favoritos" testid="header-favorites-link" />
          <IconLink to="/recargas" icon={Smartphone} label="Recargas" testid="header-topup-link" />
          <Link to="/carrinho" data-testid="header-cart-link" className="relative flex flex-col items-center gap-0.5 rounded-xl px-2.5 py-1.5 text-[11px] font-medium text-gray-600 hover:bg-bo-green/5 hover:text-bo-green">
            <ShoppingCart className="h-5 w-5" /><span className="hidden md:block">Carrinho</span>
            {count > 0 && <span className="absolute right-1 top-0 grid h-5 min-w-5 place-items-center rounded-full bg-bo-yellow px-1 text-[11px] font-bold text-bo-ink" data-testid="header-cart-count">{count}</span>}
          </Link>
          <AccountMenu />
        </div>
      </div>
      <form onSubmit={submit} className="flex items-center gap-2 px-3 pb-3 md:hidden">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Pesquisar no BÔ Market..."
          className="h-10 flex-1 rounded-full border-2 border-bo-green px-4 text-sm outline-none" data-testid="mobile-search-input" />
        <button className="grid h-10 w-10 place-items-center rounded-full bg-bo-green text-white" data-testid="mobile-search-btn"><Search className="h-4 w-4" /></button>
      </form>
    </header>
  );
}
